import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import {
  getFeatureFlags,
  getTeamFeatureFlags,
} from "@/gen_src/proto/chalk/server/v1/flag-FeatureFlagService_connectquery";
import { FeatureFlagValue } from "@/gen_src/proto/chalk/server/v1/flag_pb";
import { usePersistedGrpcQuery } from "@/utils/connect-query/query-cache-persistence/use-persisted-grpc-query";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useQuery } from "@connectrpc/connect-query";
import { useSession } from "next-auth/react";
import { createContext, useContext, useMemo } from "react";

export type FeatureFlagLiteral =
  | "feature_permissions_enabled"
  | "feature_studio"
  | "enterprise_support_enabled"
  | "metadata_data_download_disabled"
  | "open_ai_enabled"
  | "resource_usage_disabled"
  | "sql_explorer_disabled"
  | "client_fetch_offline_request_body"
  // Frontend-only flag. When "true", hides the online/offline/SQL query explorer navbar
  // links, disables their pages, and hides the inline SQL explorer surfaces. Consumers
  // treat it as "true" while flags load (see `enabledWithLoadingFallback`) so the
  // explorer surfaces never flash into view before we confirm they should be shown.
  | "disable_explorer_ui"
  | "model_registry_enabled"
  | "script_tasks_enabled"
  | "metadata_argo_workflows_enabled"
  | "custom_role_creation_enabled"
  | "scaling_groups_enabled"
  | "container_snapshots_enabled"
  | "legacy_cron_warning_disabled"
  | "stripe_billing_enabled"
  | "compute_usage_enabled"
  | "router_enabled"
  | "mcp_gateway_enabled"
  | "agent_studio_enabled"
  | "router_use_shared_proxy"
  | "mcp_gateway_use_shared_proxy"
  | "branch_source_editor_enabled"
  | "enable_isolated_workload_request_inference"
  | "default_telemetry_runtime_to_vector"
  | "online_store_config_sandbox_details_disabled"
  | "online_store_charts_disabled"
  | "metrics_export_enabled"
  | "notebooks_enabled"
  | "quotas_enabled"
  | "evaluations_enabled"
  | "evaluation_post_training_enabled"
  // Frontend-only flag. When "true", project pages use the category-rail navigation (a rail of
  // product categories, each with its own sidebar) in place of the classic sidebar, along with
  // its top rail, Settings grouping, and Compute Home landing; see `useCategoryNavEnabled`.
  | "onboard_experience"
  | "enable_offline_store_wide_table_acceleration"
  | "enable_apply_time_static_acceleration"
  // Include SQL catalog tables (a slow warehouse-introspection RPC) in the
  // command palette's "All" search. Agent @-mention surfaces use the catalog
  // regardless of this flag.
  | "command_palette_sql_catalog_enabled"
  // Frontend-only flag. When "true", SQL worksheets that offer a run mode show a single Run
  // button that always runs async, instead of the Run / Run async split button.
  | "sql_run_always_async"
  | "recovery_cluster_management_enabled";

export interface FlagCheckerV2 {
  /**
   * Check if flags are loaded. This should gate flag checks unless
   * you're okay with flashing false while the flags load.
   */
  ready: boolean;
  /**
   * Check if a particular flag is enabled. Returns "false" if the flags
   * are not loaded or if they failed to load for some reason
   */
  enabled: (flag: FeatureFlagLiteral) => boolean;

  /**
   * Like `enabled`, but returns `loadingFallback` while the flags are still loading
   * instead of implicitly treating "loading" as `false`. Use when a flag gates
   * visibility and you want a specific value to hold during load — e.g. a
   * hide-by-default surface that should stay hidden until flags confirm it is allowed.
   * Once flags are loaded (or failed to load) this behaves exactly like `enabled`.
   */
  enabledWithLoadingFallback: (flag: FeatureFlagLiteral, loadingFallback: boolean) => boolean;

  /**
   * Exact value of flag: true, false, or unset -> undefined.
   * Warning: Returns undefined if flags are not yet loaded.
   */
  exactValue: (flag: FeatureFlagLiteral) => boolean | undefined;

  /**
   * Exact value of flag with a fallback if the flag is unset.
   * Warning: Returns undefined if the flags are not yet loaded.
   */
  exactValueWithFallback: (flag: FeatureFlagLiteral, fallback: boolean) => boolean | undefined;
}

export interface FlagSummaryV2 {
  status: "not-loaded" | "loading" | "loaded" | "error";
  flags: FeatureFlagValue[] | undefined;
}

export class FlagCheckerV2Impl implements FlagCheckerV2 {
  private flagMap: { [F in FeatureFlagLiteral]?: boolean };

  constructor(
    private flagsContext: FlagSummaryV2,
    private overrides: Set<string>,
  ) {
    this.flagMap = Object.fromEntries(
      (flagsContext.flags ?? []).map((f) => [f.flag, f.value] as const),
    );
  }

  get ready(): boolean {
    return this.flagsContext.status === "loaded" || this.flagsContext.status === "error";
  }

  enabled(flag: FeatureFlagLiteral) {
    return this.flagMap[flag] ?? this.overrides.has(flag);
  }

  enabledWithLoadingFallback(flag: FeatureFlagLiteral, loadingFallback: boolean): boolean {
    if (!this.ready) return loadingFallback;
    return this.enabled(flag);
  }

  exactValue(flag: FeatureFlagLiteral): boolean | undefined {
    if (!this.ready) return undefined;
    return this.flagMap[flag];
  }

  exactValueWithFallback(flag: FeatureFlagLiteral, fallback: boolean): boolean | undefined {
    if (!this.ready) return undefined;
    return this.flagMap[flag] ?? fallback;
  }
}

/** Flags this deployment's image turns on (`FEATURE_FLAG_OVERRIDES`), whatever the server says. */
export const useFeatureFlagOverridesFromImage = (): Set<string> => {
  const { FEATURE_FLAG_OVERRIDES } = useNextEnvVars();
  return useMemo(
    () => new Set((FEATURE_FLAG_OVERRIDES || "").split(",")),
    [FEATURE_FLAG_OVERRIDES],
  );
};

export function useFlagsV2ContextQuery(): FlagCheckerV2 {
  const session = useSession();
  const { environmentId, isReady } = useRouteInfo();
  const featureFlagOverridesFromImage = useFeatureFlagOverridesFromImage();

  const environmentFlagsQuery = usePersistedGrpcQuery(getFeatureFlags, undefined, {
    enabled: session.status === "authenticated" && isReady && environmentId != null,
  });
  const teamFlagsQuery = useQuery(getTeamFeatureFlags, undefined, {
    enabled: session.status === "authenticated" && isReady && environmentId == null,
  });

  return useMemo(() => {
    // Only report "loaded" once flags actually arrived (or provably never will: a
    // resolved unauthenticated route means the query stays disabled). The old
    // `isLoading` check reported "loaded" on the first frame and while the query was
    // `enabled: false` during auth/route resolution, flashing `ready` with no flags.
    const flags =
      environmentId == null ? teamFlagsQuery.data?.flags : environmentFlagsQuery.data?.flags;
    const flagsError = environmentId == null ? teamFlagsQuery.error : environmentFlagsQuery.error;
    const status = flagsError
      ? "error"
      : flags != null
        ? "loaded"
        : !isReady || session.status === "loading" || session.status === "authenticated"
          ? "loading"
          : "loaded";
    return new FlagCheckerV2Impl(
      {
        status,
        flags,
      },
      featureFlagOverridesFromImage,
    );
  }, [
    featureFlagOverridesFromImage,
    environmentFlagsQuery.data?.flags,
    environmentFlagsQuery.error,
    environmentId,
    isReady,
    session.status,
    teamFlagsQuery.data?.flags,
    teamFlagsQuery.error,
  ]);
}

export const FlagsV2Context = createContext<FlagCheckerV2>(
  new FlagCheckerV2Impl({ status: "not-loaded", flags: undefined }, new Set<string>()),
);

export function useFlagsV2() {
  return useContext(FlagsV2Context);
}
